/**
 * Cart Drawer Web Component
 * Slide-from-right cart drawer with Cart API + Section Rendering API integration.
 * Native Shopify cart drawer with full Cart API integration.
 *
 * Events:
 * - Listens: `cart:open` on document (opens the drawer)
 * - Dispatches: `cart:updated` on document (after any cart change)
 *
 * @extends ABBaseComponent
 */
import { ABBaseComponent, defineComponent } from './ab-base-component.js';

class ABCartDrawer extends ABBaseComponent {
  connectedCallback() {
    // Cache DOM refs
    this._overlay = this.$('[data-cart-drawer-overlay]');
    this._dialog = this.$('.ab-cart-drawer__dialog');
    this._items = this.$('[data-cart-items]');
    this._footer = this.$('[data-cart-footer]');
    this._status = this.$('[data-cart-status]');
    this._countEl = this.$('[data-cart-count]');
    this._shippingBar = this.$('[data-shipping-bar]');
    this._closeBtn = this.$('[data-cart-drawer-close]');

    // State
    this._triggerEl = null;
    this._focusTrapHandler = null;
    this._savedScrollY = 0;
    this._debounceTimers = new Map();
    this._isUpdating = false;

    // Bind events
    this._onOverlayClick = this._onOverlayClick.bind(this);
    this._onKeydown = this._onKeydown.bind(this);
    this._onClick = this._onClick.bind(this);
    this._onCartOpen = this._onCartOpen.bind(this);
    this._onDiscountKeydown = this._onDiscountKeydown.bind(this);

    this._overlay?.addEventListener('click', this._onOverlayClick);
    this._closeBtn?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', this._onKeydown);
    this.addEventListener('click', this._onClick);
    document.addEventListener('cart:open', this._onCartOpen);

    // Discount input enter key
    const discountInput = this.$('[data-discount-input]');
    if (discountInput) {
      discountInput.addEventListener('keydown', this._onDiscountKeydown);
    }

    // ATC form interception — intercept any form[action*="/cart/add"] submit
    this._onFormSubmit = this._onFormSubmit.bind(this);
    document.addEventListener('submit', this._onFormSubmit);

    // Recs add button handler
    this._onRecsClick = this._onRecsClick.bind(this);
    const recsContainer = this.$('[data-cart-recs]');
    if (recsContainer) {
      recsContainer.addEventListener('click', this._onRecsClick);
    }

    // Track last-added product ID for recommendations
    this._lastProductId = null;

    // Fetch interceptor — catch third-party cart additions
    this._installFetchInterceptor();
  }

  disconnectedCallback() {
    this._overlay?.removeEventListener('click', this._onOverlayClick);
    document.removeEventListener('keydown', this._onKeydown);
    this.removeEventListener('click', this._onClick);
    document.removeEventListener('cart:open', this._onCartOpen);

    const discountInput = this.$('[data-discount-input]');
    if (discountInput) {
      discountInput.removeEventListener('keydown', this._onDiscountKeydown);
    }

    document.removeEventListener('submit', this._onFormSubmit);

    const recsContainer = this.$('[data-cart-recs]');
    if (recsContainer) {
      recsContainer.removeEventListener('click', this._onRecsClick);
    }

    this._releaseFocus();
    this._debounceTimers.forEach(t => clearTimeout(t));
    this._debounceTimers.clear();
  }

  // ─── Event handlers ───────────────────────────────

  _onOverlayClick() {
    this.close();
  }

  _onKeydown(e) {
    if (e.key === 'Escape' && this.classList.contains('is-open')) {
      e.preventDefault();
      this.close();
    }
  }

  _onCartOpen(e) {
    // Optional: e.detail can contain { formData } for addToCart
    if (e.detail?.formData) {
      this.addToCart(e.detail.formData);
    } else {
      this.open();
    }
  }

  _onDiscountKeydown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      const code = e.target.value.trim();
      if (code) this.applyDiscount(code);
    }
  }

  _onClick(e) {
    const actionBtn = e.target.closest('[data-action]');
    if (!actionBtn) {
      // Check for apply discount button
      const applyBtn = e.target.closest('[data-apply-discount]');
      if (applyBtn) {
        const input = this.$('[data-discount-input]');
        const code = input?.value.trim();
        if (code) this.applyDiscount(code);
      }
      return;
    }

    e.preventDefault();
    const action = actionBtn.dataset.action;
    const key = actionBtn.dataset.key;

    if (!key) return;

    switch (action) {
      case 'increase': {
        const input = this._getQtyInput(key);
        const qty = parseInt(input?.value || '0', 10);
        this._debouncedUpdate(key, qty + 1);
        break;
      }
      case 'decrease': {
        const input = this._getQtyInput(key);
        const qty = parseInt(input?.value || '0', 10);
        this._debouncedUpdate(key, Math.max(0, qty - 1));
        break;
      }
      case 'remove':
        this.removeItem(key);
        break;
    }
  }

  // ─── Open / Close ─────────────────────────────────

  open() {
    this._triggerEl = document.activeElement;
    this.classList.add('is-open');
    document.body.classList.add('ab-cart-drawer-open');
    this._dialog?.setAttribute('aria-hidden', 'false');

    // iOS Safari scroll lock
    this._savedScrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this._savedScrollY}px`;
    document.body.style.width = '100%';

    // Focus the dialog
    this._dialog?.focus();

    // Activate focus trap
    this._trapFocus();

    // Announce
    this._announce('Cart drawer opened');

    // Lazy-load cross-sell recommendations on drawer open
    this._fetchRecommendations();
  }

  close() {
    this.classList.remove('is-open');
    document.body.classList.remove('ab-cart-drawer-open');
    this._dialog?.setAttribute('aria-hidden', 'true');

    // iOS Safari restore scroll
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, this._savedScrollY);

    // Release focus trap
    this._releaseFocus();

    // Return focus
    if (this._triggerEl && typeof this._triggerEl.focus === 'function') {
      this._triggerEl.focus();
    }

    // Announce
    this._announce('Cart drawer closed');
  }

  // ─── Focus trap ───────────────────────────────────

  _trapFocus() {
    this._releaseFocus();

    this._focusTrapHandler = (e) => {
      if (e.key !== 'Tab') return;

      const focusable = this._dialog?.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable || focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', this._focusTrapHandler);
  }

  _releaseFocus() {
    if (this._focusTrapHandler) {
      document.removeEventListener('keydown', this._focusTrapHandler);
      this._focusTrapHandler = null;
    }
  }

  // ─── ATC Form Interception ──────────────────────

  _onFormSubmit(event) {
    const form = event.target.closest('form[action*="/cart/add"]');
    if (!form) return;

    event.preventDefault();

    const variantInput = form.querySelector('input[name="id"]');
    if (!variantInput) return;

    const submitBtn = form.querySelector('[type="submit"]');
    const originalText = submitBtn?.textContent;

    // Disable button and show "Adding..."
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = 'Adding...';
    }

    // Build cart item from form data
    const item = {
      id: parseInt(variantInput.value, 10),
      quantity: parseInt(form.querySelector('input[name="quantity"]')?.value || '1', 10)
    };

    // Extract line item properties (for Maestrooo customizer compatibility)
    const properties = {};
    const propInputs = form.querySelectorAll('[name^="properties["]');
    propInputs.forEach(input => {
      const match = input.name.match(/properties\[(.+?)\]/);
      if (match && input.value) {
        properties[match[1]] = input.value;
      }
    });
    if (Object.keys(properties).length > 0) {
      item.properties = properties;
    }

    // Capture product ID for recommendations
    const productEl = form.closest('[data-product-id]');
    if (productEl) {
      this._lastProductId = productEl.dataset.productId;
    }

    this.addToCart(item)
      .then(() => {
        if (submitBtn) {
          submitBtn.textContent = 'Added!';
          setTimeout(() => {
            submitBtn.disabled = false;
            submitBtn.textContent = originalText;
          }, 1500);
        }
      })
      .catch(() => {
        if (submitBtn) {
          submitBtn.textContent = 'Error — try again';
          setTimeout(() => {
            submitBtn.disabled = false;
            submitBtn.textContent = originalText;
          }, 2000);
        }
      });
  }

  _onRecsClick(event) {
    const addBtn = event.target.closest('[data-rec-add]');
    if (!addBtn) return;

    event.preventDefault();

    const variantId = parseInt(addBtn.dataset.recAdd, 10);
    if (!variantId) return;

    const recSource = addBtn.dataset.recSource || 'cart_drawer';

    addBtn.disabled = true;
    addBtn.textContent = 'Adding...';

    // Track recommendation click
    this._pushDataLayer('rec_add_to_cart', {
      rec_surface: recSource,
      rec_variant_id: variantId
    });

    this.addToCart({
      id: variantId,
      quantity: 1,
      properties: { '_rec_source': recSource }
    })
      .then(() => {
        addBtn.textContent = 'Added!';
        setTimeout(() => {
          addBtn.disabled = false;
          addBtn.textContent = 'Add';
        }, 1500);
      })
      .catch(() => {
        addBtn.disabled = false;
        addBtn.textContent = 'Add';
      });
  }

  // ─── Cross-sell Recommendations ────────────────

  async _fetchRecommendations() {
    const recsCount = parseInt(this.dataset.recsCount || '0', 10);
    if (recsCount === 0) return;

    const recsContainer = this.$('[data-cart-recs]');
    if (!recsContainer) return;

    // If metafield recs are already server-rendered, skip
    if (recsContainer.querySelector('[data-has-metafield-recs]')) {
      this._trackRecImpressions(recsContainer);
      return;
    }

    // Fallback: re-render section via Section Rendering API.
    // This runs the same Liquid metafield rec logic as the initial page render,
    // ensuring type diversity, finish matching, and designer collection headings
    // from our recommendation engine — NOT Shopify's generic "related" API.
    try {
      const sectionId = this._getSectionId();
      const response = await fetch(`/cart?sections=${sectionId}`);
      if (!response.ok) throw new Error(`Section re-render failed: ${response.status}`);

      const sectionData = await response.json();
      const html = sectionData[sectionId];
      if (!html) return;

      // Parse the server-rendered HTML and extract the recs container.
      // This HTML comes from our own Liquid section (trusted, same-origin Shopify CDN),
      // using the same pattern as _renderSections() and _refreshAndOpen().
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newRecs = doc.querySelector('[data-cart-recs]');

      if (newRecs && newRecs.children.length > 0) {
        // Replace recs content with server-rendered metafield recs
        while (recsContainer.firstChild) recsContainer.removeChild(recsContainer.firstChild);
        while (newRecs.firstChild) recsContainer.appendChild(newRecs.firstChild);
        this._trackRecImpressions(recsContainer);
      }
    } catch (err) {
      console.error('[ABCartDrawer] _fetchRecommendations error:', err);
    }
  }

  _escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  // ─── Fetch Interceptor (third-party ATC) ─────────

  _installFetchInterceptor() {
    if (window.__abCartFetchPatched) return;
    window.__abCartFetchPatched = true;

    const originalFetch = window.fetch;
    const drawer = this;

    window.fetch = function(input, init) {
      const url = typeof input === 'string' ? input : input?.url || '';
      const isCartAdd = url.includes('/cart/add');

      // Only intercept if this is NOT from our own cart drawer
      if (isCartAdd && !window.__abCartDrawerAdding) {
        return originalFetch.apply(this, arguments).then(response => {
          if (response.ok) {
            drawer._refreshAndOpen();
          }
          return response;
        });
      }

      return originalFetch.apply(this, arguments);
    };

    // Also intercept XMLHttpRequest (third-party apps may use XHR)
    const originalXhrOpen = XMLHttpRequest.prototype.open;
    const originalXhrSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function(method, url) {
      this._abUrl = url;
      return originalXhrOpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function() {
      if (this._abUrl && this._abUrl.includes('/cart/add') && !window.__abCartDrawerAdding) {
        this.addEventListener('load', function() {
          if (this.status >= 200 && this.status < 300) {
            drawer._refreshAndOpen();
          }
        });
      }
      return originalXhrSend.apply(this, arguments);
    };
  }

  /**
   * Refresh cart drawer contents via Section Rendering API and open it.
   * Used by the fetch/XHR interceptor when third-party code adds to cart.
   */
  _refreshAndOpen() {
    const sectionId = this._getSectionId();
    fetch(`/cart?sections=${sectionId}`)
      .then(r => r.json())
      .then(sectionData => {
        if (sectionData[sectionId]) {
          this._renderSections(sectionData);
        }
        this.open();
        return fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } });
      })
      .then(r => r.json())
      .then(cart => {
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { cart, source: 'fetch-interceptor' }
        }));
      })
      .catch(err => console.error('[ABCartDrawer] _refreshAndOpen error:', err));
  }

  // ─── Cart API ─────────────────────────────────────

  _getSectionId() {
    return this.dataset.sectionId || 'cart-drawer';
  }

  async updateQuantity(key, quantity) {
    if (this._isUpdating) return;
    this._isUpdating = true;

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: key,
          quantity,
          sections: this._getSectionId(),
          sections_url: '/cart'
        })
      });

      if (!response.ok) throw new Error(`Cart update failed: ${response.status}`);

      const data = await response.json();

      if (data.sections && data.sections[this._getSectionId()]) {
        this._renderSections(data.sections);
      }

      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { cart: data, source: 'cart-drawer' }
      }));

      this._announce(`Cart updated, ${data.item_count} ${data.item_count === 1 ? 'item' : 'items'} in cart`);
    } catch (err) {
      console.error('[ABCartDrawer] updateQuantity error:', err);
      this._announce('Error updating cart. Please try again.');
    } finally {
      this._isUpdating = false;
    }
  }

  async removeItem(key) {
    await this.updateQuantity(key, 0);
  }

  async addToCart(formData) {
    try {
      const items = formData instanceof FormData
        ? Object.fromEntries(formData)
        : formData;

      const body = Array.isArray(items)
        ? { items, sections: this._getSectionId(), sections_url: '/cart' }
        : { items: [items], sections: this._getSectionId(), sections_url: '/cart' };

      window.__abCartDrawerAdding = true;
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      window.__abCartDrawerAdding = false;

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.description || `Add to cart failed: ${response.status}`);
      }

      const data = await response.json();

      if (data.sections && data.sections[this._getSectionId()]) {
        this._renderSections(data.sections);
      }

      this.open();

      // Highlight newly added item
      if (data.items && data.items.length > 0) {
        // Short delay to ensure DOM has updated
        requestAnimationFrame(() => {
          this._highlightNewItem(data.items[0].key);
        });
      }

      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { cart: data, source: 'cart-drawer' }
      }));

      this._announce(`Added to cart. ${data.items?.[0]?.title || 'Item'} added.`);
    } catch (err) {
      window.__abCartDrawerAdding = false;
      console.error('[ABCartDrawer] addToCart error:', err);
      this._announce(err.message || 'Error adding to cart. Please try again.');

      // Dispatch error event for ATC buttons to handle
      document.dispatchEvent(new CustomEvent('cart:error', {
        detail: { message: err.message, source: 'cart-drawer' }
      }));

      // Re-throw so callers (e.g. _onFormSubmit) can handle button state
      throw err;
    }
  }

  async applyDiscount(code) {
    const statusEl = this.$('[data-discount-status]');

    try {
      const response = await fetch('/discount/' + encodeURIComponent(code));
      // Shopify discount URL redirects — we need to then fetch the cart
      // to get updated sections
      const cartResponse = await fetch('/cart.js', {
        headers: { 'Content-Type': 'application/json' }
      });
      const cart = await cartResponse.json();

      // Re-fetch with section rendering
      const sectionResponse = await fetch(`/cart?sections=${this._getSectionId()}`);
      const sectionData = await sectionResponse.json();

      if (sectionData[this._getSectionId()]) {
        this._renderSections(sectionData);
      }

      if (cart.total_discount > 0) {
        if (statusEl) {
          statusEl.textContent = 'Discount applied!';
          statusEl.className = 'ab-cart-drawer__discount-status is-success';
        }
      } else {
        if (statusEl) {
          statusEl.textContent = 'Invalid or expired code';
          statusEl.className = 'ab-cart-drawer__discount-status is-error';
        }
      }

      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { cart, source: 'cart-drawer' }
      }));
    } catch (err) {
      console.error('[ABCartDrawer] applyDiscount error:', err);
      if (statusEl) {
        statusEl.textContent = 'Error applying discount. Please try again.';
        statusEl.className = 'ab-cart-drawer__discount-status is-error';
      }
    }
  }

  // ─── Section Rendering ────────────────────────────

  _renderSections(sections) {
    const html = sections[this._getSectionId()];
    if (!html) return;

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Selective DOM replacement to preserve focus and animation state
    const newItems = doc.querySelector('[data-cart-items]');
    const newFooter = doc.querySelector('[data-cart-footer]');
    const newShipping = doc.querySelector('[data-shipping-bar]');
    const newCount = doc.querySelector('[data-cart-count]');
    const newRecs = doc.querySelector('[data-cart-recs]');

    if (newItems && this._items) {
      this._items.innerHTML = newItems.innerHTML;
    }

    if (newFooter && this._footer) {
      this._footer.innerHTML = newFooter.innerHTML;
      // Re-bind discount input keydown after DOM replacement
      const discountInput = this.$('[data-discount-input]');
      if (discountInput) {
        discountInput.addEventListener('keydown', this._onDiscountKeydown);
      }
    }

    if (newShipping && this._shippingBar) {
      this._shippingBar.innerHTML = newShipping.innerHTML;
    } else if (!newShipping && this._shippingBar) {
      // Shipping bar might be removed if threshold setting changed
    }

    if (newCount && this._countEl) {
      this._countEl.textContent = newCount.textContent;
    }

    // Update server-rendered recommendations from metafields
    const recsContainer = this.$('[data-cart-recs]');
    if (newRecs && recsContainer) {
      recsContainer.innerHTML = newRecs.innerHTML;
    }
  }

  // ─── Helpers ──────────────────────────────────────

  _highlightNewItem(key) {
    const item = this.querySelector(`[data-cart-item][data-key="${key}"]`);
    if (!item) return;

    item.setAttribute('data-just-added', '');
    item.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

    setTimeout(() => {
      item.removeAttribute('data-just-added');
    }, 2500);
  }

  _getQtyInput(key) {
    return this.querySelector(`.ab-cart-drawer-item__qty-input[data-key="${key}"]`);
  }

  _debouncedUpdate(key, quantity) {
    // Update the input value immediately for responsiveness
    const input = this._getQtyInput(key);
    if (input) input.value = quantity;

    // Clear previous timer for this key
    if (this._debounceTimers.has(key)) {
      clearTimeout(this._debounceTimers.get(key));
    }

    // Set new debounced call
    const timer = setTimeout(() => {
      this._debounceTimers.delete(key);
      this.updateQuantity(key, quantity);
    }, 300);

    this._debounceTimers.set(key, timer);
  }

  // ─── Analytics ───────────────────────────────────

  _trackRecImpressions(container) {
    const cards = container.querySelectorAll('[data-rec-card]');
    cards.forEach((card, i) => {
      const addBtn = card.querySelector('[data-rec-add]');
      if (addBtn) {
        this._pushDataLayer('rec_impression', {
          rec_surface: addBtn.dataset.recSource || 'cart_drawer',
          rec_variant_id: addBtn.dataset.recAdd,
          rec_position: i + 1
        });
      }
    });
  }

  _pushDataLayer(event, data) {
    if (typeof window.dataLayer !== 'undefined') {
      window.dataLayer.push({ event, ...data });
    }
  }

  _announce(message) {
    if (this._status) {
      this._status.textContent = '';
      // Force screen reader re-announcement by clearing then setting
      requestAnimationFrame(() => {
        this._status.textContent = message;
      });
    }
  }
}

defineComponent('ab-cart-drawer', ABCartDrawer);
